import React, { useCallback, useEffect, useState } from 'react';
import {
  FiArrowLeft, FiCheck, FiCheckCircle, FiCircle, FiLink, FiLock, FiUnlock,
  FiChevronDown, FiChevronUp, FiAlertTriangle, FiEdit2, FiTrash2, FiCornerUpLeft, FiRotateCcw,
} from 'react-icons/fi';
import {
  buscarProcesso, atualizarProcesso, concluirEtapaProcesso,
  excluirProcesso, reabrirProcesso, voltarEtapaProcesso,
} from '../../services/processos';
import { carregarCatalogo } from '../../services/processoTipos';
import { etapasDoTipo, etapaPorNumero, labelTipo, labelStatus } from './catalogoUtils';
import ProcessoFormModal from './ProcessoFormModal';
import ConfirmarModal from './ConfirmarModal';
import styles from './ProcessoDetalhe.module.css';

/* painel "Como executar esta etapa" -- só renderiza o que a etapa tiver
   (objetivo/como_fazer/documentos/escalar são todos opcionais no
   catálogo); some por inteiro se a etapa não tiver nada disso */
function OrientacaoEtapa({ etapa, abertoPadrao }) {
  const [aberto, setAberto] = useState(abertoPadrao);
  if (!etapa) return null;

  const temConteudo = etapa.objetivo || etapa.como_fazer?.length || etapa.documentos?.length || etapa.escalar;
  if (!temConteudo) return null;

  return (
    <div className={styles.orientacao}>
      <button type="button" className={styles.orientacaoToggle} onClick={() => setAberto(a => !a)}>
        {aberto ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
        Como executar esta etapa
      </button>
      {aberto && (
        <div className={styles.orientacaoCorpo}>
          {etapa.objetivo && <p className={styles.orientacaoObjetivo}>{etapa.objetivo}</p>}

          {etapa.como_fazer?.length > 0 && (
            <ul className={styles.orientacaoLista}>
              {etapa.como_fazer.map((passo, i) => <li key={i}>{passo}</li>)}
            </ul>
          )}

          {etapa.documentos?.length > 0 && (
            <div>
              <span className={styles.orientacaoSubtitulo}>Documentos</span>
              <ul className={styles.orientacaoLista}>
                {etapa.documentos.map((doc, i) => <li key={i}>{doc}</li>)}
              </ul>
            </div>
          )}

          {etapa.escalar && (
            <div className={styles.orientacaoAlerta}>
              <FiAlertTriangle size={14} />
              <span><strong>Parar e perguntar:</strong> {etapa.escalar}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatarDocumento(doc) {
  if (!doc) return '';
  const d = doc.replace(/\D/g, '');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  return doc;
}

function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatarDataHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR');
}

const SITUACOES_LICENCIAMENTO = [
  { valor: 'regular',       label: 'Regular' },
  { valor: 'irregular',     label: 'Irregular' },
  { valor: 'dispensado',    label: 'Dispensado' },
  { valor: 'nao_analisado', label: 'Não analisado' },
];

function ProcessoDetalhe({ processoId, onVoltar }) {
  const [processo, setProcesso]     = useState(null);
  const [catalogo, setCatalogo]     = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');

  const [observacaoEtapa, setObservacaoEtapa] = useState('');
  const [linkEtapa, setLinkEtapa]             = useState('');
  const [responsavelEtapa, setResponsavelEtapa] = useState('');
  const [resultado, setResultado]             = useState('');
  const [situacaoLic, setSituacaoLic]         = useState('');
  const [vencAvcb, setVencAvcb]               = useState('');
  const [vencLicenca, setVencLicenca]         = useState('');
  const [concluindo, setConcluindo]           = useState(false);
  const [erroEtapa, setErroEtapa]             = useState('');

  const [pendencia, setPendencia]     = useState('');
  const [salvandoPendencia, setSalvandoPendencia] = useState(false);
  const [alternandoTravado, setAlternandoTravado] = useState(false);

  const [mostrarEditar, setMostrarEditar]   = useState(false);
  const [mostrarExcluir, setMostrarExcluir] = useState(false);
  const [excluindoProcesso, setExcluindoProcesso] = useState(false);
  const [erroExcluir, setErroExcluir]       = useState('');
  const [voltandoEtapa, setVoltandoEtapa]   = useState(false);
  const [reabrindo, setReabrindo]           = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    Promise.all([buscarProcesso(processoId), carregarCatalogo()])
      .then(([p, cat]) => {
        setProcesso(p);
        setCatalogo(cat);
        setPendencia(p.pendencia || '');
        setResponsavelEtapa(p.responsavel || '');
      })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [processoId]);

  useEffect(() => { carregar(); }, [carregar]);

  async function concluirEtapa() {
    const etapas = etapasDoTipo(catalogo, processo.tipo);
    const ultimaEtapa = processo.etapa_atual >= etapas.length;
    if (ultimaEtapa && !resultado.trim()) {
      setErroEtapa('Informe o resultado para concluir o processo.');
      return;
    }
    setConcluindo(true); setErroEtapa('');
    try {
      const atualizado = await concluirEtapaProcesso(processoId, {
        numero_etapa: processo.etapa_atual,
        nome_etapa: etapas[processo.etapa_atual - 1]?.nome || `Etapa ${processo.etapa_atual}`,
        observacao: observacaoEtapa,
        link_documento: linkEtapa,
        responsavel: responsavelEtapa,
        ultima_etapa: ultimaEtapa,
        ...(ultimaEtapa ? {
          resultado,
          ...(processo.tipo === 'licenciamento' ? {
            situacao_licenciamento: situacaoLic || undefined,
            vencimento_avcb: vencAvcb || undefined,
            vencimento_licenca: vencLicenca || undefined,
          } : {}),
        } : {}),
      });
      setProcesso(atualizado);
      setObservacaoEtapa(''); setLinkEtapa(''); setResultado('');
      setSituacaoLic(''); setVencAvcb(''); setVencLicenca('');
      carregar();
    } catch (e) {
      setErroEtapa(e.message);
    } finally {
      setConcluindo(false);
    }
  }

  async function salvarPendencia() {
    setSalvandoPendencia(true);
    try {
      const atualizado = await atualizarProcesso(processoId, { pendencia });
      setProcesso(prev => ({ ...prev, pendencia: atualizado.pendencia }));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoPendencia(false);
    }
  }

  async function alternarTravado() {
    setAlternandoTravado(true);
    try {
      const novoStatus = processo.status === 'travado' ? 'em_andamento' : 'travado';
      const atualizado = await atualizarProcesso(processoId, { status: novoStatus });
      setProcesso(prev => ({ ...prev, status: atualizado.status }));
    } catch (e) {
      setErro(e.message);
    } finally {
      setAlternandoTravado(false);
    }
  }

  function aoSalvarEdicao() {
    setMostrarEditar(false);
    carregar();
  }

  async function excluirProcessoAtual() {
    setExcluindoProcesso(true); setErroExcluir('');
    try {
      await excluirProcesso(processoId);
      onVoltar();
    } catch (e) {
      setErroExcluir(e.message);
    } finally {
      setExcluindoProcesso(false);
    }
  }

  async function reabrirProcessoAtual() {
    setReabrindo(true); setErro('');
    try {
      await reabrirProcesso(processoId);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setReabrindo(false);
    }
  }

  async function voltarEtapaAtual() {
    setVoltandoEtapa(true); setErro('');
    try {
      await voltarEtapaProcesso(processoId);
      setObservacaoEtapa(''); setLinkEtapa(''); setResultado('');
      setSituacaoLic(''); setVencAvcb(''); setVencLicenca('');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setVoltandoEtapa(false);
    }
  }

  if (carregando) return <div className={styles.page}><p className={styles.vazio}>Carregando...</p></div>;
  if (erro || !processo) return (
    <div className={styles.page}>
      <button className={styles.btnVoltar} onClick={onVoltar} type="button"><FiArrowLeft size={14} /> Voltar</button>
      <div className={styles.erroFaixa}>{erro || 'Processo não encontrado.'}</div>
    </div>
  );

  const etapas = etapasDoTipo(catalogo, processo.tipo);
  const finalizado = processo.status === 'concluido' || processo.status === 'cancelado';
  const ultimaEtapa = processo.etapa_atual >= etapas.length;

  return (
    <div className={styles.page}>
      <button className={styles.btnVoltar} onClick={onVoltar} type="button"><FiArrowLeft size={14} /> Voltar</button>

      <div className={styles.cabecalho}>
        <div>
          <h1 className={styles.clienteNome}>{processo.cliente_nome || '—'}</h1>
          <p className={styles.clienteDoc}>{formatarDocumento(processo.cliente_cnpj || processo.cliente_cpf) || 'Sem documento cadastrado'}</p>
        </div>
        <div className={styles.cabecalhoInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Tipo</span>
            <span className={styles.infoValor}>{labelTipo(catalogo, processo.tipo)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Status</span>
            <span className={`${styles.statusBadge} ${styles['status_' + processo.status]}`}>{labelStatus(processo.status)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Abertura</span>
            <span className={styles.infoValor}>{formatarData(processo.data_abertura)}</span>
          </div>
        </div>
      </div>

      <div className={styles.cabecalhoAcoes}>
        <button className={styles.btnSecundario} onClick={() => setMostrarEditar(true)} type="button">
          <FiEdit2 size={13} /> Editar
        </button>
        <button className={styles.btnSecundario} onClick={() => { setErroExcluir(''); setMostrarExcluir(true); }} type="button">
          <FiTrash2 size={13} /> Excluir
        </button>
        {processo.etapa_atual > 1 && (
          <button className={styles.btnSecundario} onClick={voltarEtapaAtual} disabled={voltandoEtapa} type="button">
            <FiCornerUpLeft size={13} /> {voltandoEtapa ? 'Voltando...' : 'Voltar etapa'}
          </button>
        )}
        {finalizado && (
          <button className={styles.btnSecundario} onClick={reabrirProcessoAtual} disabled={reabrindo} type="button">
            <FiRotateCcw size={13} /> {reabrindo ? 'Reabrindo...' : 'Reabrir'}
          </button>
        )}
      </div>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}

      <div className={styles.blocoPendencia}>
        <div className={styles.campo}>
          <span className={styles.label}>Pendência</span>
          <textarea
            className={styles.textarea}
            rows={2}
            value={pendencia}
            onChange={e => setPendencia(e.target.value)}
            placeholder="O que está impedindo o avanço deste processo?"
          />
        </div>
        <div className={styles.blocoPendenciaAcoes}>
          <button className={styles.btnSecundario} onClick={salvarPendencia} disabled={salvandoPendencia} type="button">
            {salvandoPendencia ? 'Salvando...' : 'Salvar pendência'}
          </button>
          {!finalizado && (
            <button className={styles.btnSecundario} onClick={alternarTravado} disabled={alternandoTravado} type="button">
              {processo.status === 'travado'
                ? <><FiUnlock size={13} /> Destravar processo</>
                : <><FiLock size={13} /> Marcar como travado</>}
            </button>
          )}
        </div>
      </div>

      <div className={styles.etapasWrap}>
        <h2 className={styles.secaoTitulo}>Etapas</h2>
        <div className={styles.etapasLista}>
          {etapas.map((etapa, i) => {
            const numero = i + 1;
            const concluida = processo.etapa_atual > numero || (finalizado && numero <= processo.etapa_atual);
            const atual = !finalizado && numero === processo.etapa_atual;
            return (
              <div key={numero} className={`${styles.etapaItem} ${atual ? styles.etapaAtual : ''}`}>
                <div className={styles.etapaMarca}>
                  {concluida ? <FiCheckCircle size={18} className={styles.iconeConcluida} /> : <FiCircle size={18} className={styles.iconePendente} />}
                </div>
                <div className={styles.etapaCorpo}>
                  <p className={styles.etapaNome}>{numero}. {etapa.nome}</p>

                  {atual && <OrientacaoEtapa etapa={etapa} abertoPadrao />}

                  {atual && (
                    <div className={styles.etapaForm}>
                      {erroEtapa && <p className={styles.erro}>{erroEtapa}</p>}

                      <div className={styles.campo}>
                        <span className={styles.label}>Observação</span>
                        <textarea
                          className={styles.textarea}
                          rows={2}
                          value={observacaoEtapa}
                          onChange={e => setObservacaoEtapa(e.target.value)}
                        />
                      </div>

                      <div className={styles.campoLinha}>
                        <div className={styles.campo}>
                          <span className={styles.label}>Link do documento</span>
                          <input className={styles.input} value={linkEtapa} onChange={e => setLinkEtapa(e.target.value)} placeholder="https://..." />
                        </div>
                        <div className={styles.campo}>
                          <span className={styles.label}>Responsável</span>
                          <input className={styles.input} value={responsavelEtapa} onChange={e => setResponsavelEtapa(e.target.value)} />
                        </div>
                      </div>

                      {ultimaEtapa && (
                        <>
                          <div className={styles.campo}>
                            <span className={styles.label}>Resultado</span>
                            <textarea
                              className={styles.textarea}
                              rows={2}
                              value={resultado}
                              onChange={e => setResultado(e.target.value)}
                              placeholder="Como o processo foi concluído"
                            />
                          </div>

                          {processo.tipo === 'licenciamento' && (
                            <div className={styles.campoLinha}>
                              <div className={styles.campo}>
                                <span className={styles.label}>Situação do licenciamento</span>
                                <select className={styles.input} value={situacaoLic} onChange={e => setSituacaoLic(e.target.value)}>
                                  <option value="">Não informar</option>
                                  {SITUACOES_LICENCIAMENTO.map(s => <option key={s.valor} value={s.valor}>{s.label}</option>)}
                                </select>
                              </div>
                              <div className={styles.campo}>
                                <span className={styles.label}>Vencimento AVCB</span>
                                <input type="date" className={styles.input} value={vencAvcb} onChange={e => setVencAvcb(e.target.value)} />
                              </div>
                              <div className={styles.campo}>
                                <span className={styles.label}>Vencimento licença</span>
                                <input type="date" className={styles.input} value={vencLicenca} onChange={e => setVencLicenca(e.target.value)} />
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <button className={styles.btnConcluir} onClick={concluirEtapa} disabled={concluindo} type="button">
                        <FiCheck size={14} /> {concluindo ? 'Concluindo...' : ultimaEtapa ? 'Concluir processo' : 'Concluir etapa'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {processo.resultado && (
        <div className={styles.blocoResultado}>
          <h2 className={styles.secaoTitulo}>Resultado</h2>
          <p>{processo.resultado}</p>
          {processo.data_conclusao && <p className={styles.resultadoData}>Concluído em {formatarData(processo.data_conclusao)}</p>}
        </div>
      )}

      <div className={styles.historico}>
        <h2 className={styles.secaoTitulo}>Histórico de etapas</h2>
        {(!processo.etapas || processo.etapas.length === 0) ? (
          <p className={styles.vazio}>Nenhuma etapa concluída ainda.</p>
        ) : (
          <div className={styles.historicoLista}>
            {processo.etapas.map(e => (
              <div key={e.id} className={styles.historicoItem}>
                <div className={styles.historicoTopo}>
                  <span className={styles.historicoNome}>{e.numero_etapa}. {e.nome_etapa}</span>
                  <span className={styles.historicoData}>{formatarDataHora(e.data_conclusao)}</span>
                </div>
                {e.responsavel && <p className={styles.historicoResponsavel}>Responsável: {e.responsavel}</p>}
                {e.observacao && <p className={styles.historicoObs}>{e.observacao}</p>}
                {e.link_documento && (
                  <a className={styles.historicoLink} href={e.link_documento} target="_blank" rel="noopener noreferrer">
                    <FiLink size={12} /> Ver documento
                  </a>
                )}
                <OrientacaoEtapa etapa={etapaPorNumero(catalogo, processo.tipo, e.numero_etapa)} abertoPadrao={false} />
              </div>
            ))}
          </div>
        )}
      </div>

      {mostrarEditar && (
        <ProcessoFormModal catalogo={catalogo} processo={processo} onFechar={() => setMostrarEditar(false)} onSalvo={aoSalvarEdicao} />
      )}

      {mostrarExcluir && (
        <ConfirmarModal
          titulo="Excluir processo"
          mensagem="O histórico de etapas será apagado junto. Esta ação não pode ser desfeita."
          textoConfirmar="Excluir"
          perigo
          confirmando={excluindoProcesso}
          erro={erroExcluir}
          onConfirmar={excluirProcessoAtual}
          onCancelar={() => setMostrarExcluir(false)}
        />
      )}
    </div>
  );
}

export default ProcessoDetalhe;
